import { Button, Text } from '@change/ds-core-ui';
import { ChangeFlagIcon } from '@change/ds-icons/ChangeFlagIcon';
import { MapIcon } from '@change/ds-icons/MapIcon';
import { TrendingIcon } from '@change/ds-icons/TrendingIcon';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { useExploreButtons } from './hooks/useExploreButtons';

export const ExploreButtons: React.FC = () => {
	return (
		<ClientRender placeholder={<ExploreButtonsView disabled />}>
			<ExploreButtonsInteractive />
		</ClientRender>
	);
};

const ExploreButtonsInteractive: React.FC = () => {
	const {
		data: { isNearYouLoading },
		actions: { filterByNearYou, filterByTrending, filterByVictories },
	} = useExploreButtons();

	return (
		<ExploreButtonsView
			isNearYouLoading={isNearYouLoading}
			filterByNearYou={filterByNearYou}
			filterByTrending={filterByTrending}
			filterByVictories={filterByVictories}
		/>
	);
};

type ExploreButtonsViewProps = {
	disabled?: boolean;
	isNearYouLoading?: boolean;
	filterByNearYou?: () => void;
	filterByTrending?: () => void;
	filterByVictories?: () => void;
};

const ExploreButtonsView: React.FC<ExploreButtonsViewProps> = ({
	disabled,
	isNearYouLoading,
	filterByNearYou,
	filterByTrending,
	filterByVictories,
}) => {
	return (
		<div className="mt-8 md:mt-16">
			<div className="container">
				<Text variant="heading" as="h2" size="small" className="mt-0 mb-6 hidden md:block">
					<Translate value="corgi.search.explore.title" />
				</Text>
				<div className="grid grid-cols-3 gap-6">
					<ExploreButton
						icon={<MapIcon size="x-large" gradient />}
						text={
							<Text variant="body" as="span" size="large" className="text-center font-semibold">
								<Translate value="corgi.search.explore.near_you" />
							</Text>
						}
						onClick={filterByNearYou}
						disabled={disabled || isNearYouLoading}
						data-qa="explore-near-you-button"
					/>
					<ExploreButton
						icon={<TrendingIcon size="x-large" gradient />}
						text={
							<Text variant="body" as="span" size="large" className="text-center font-semibold">
								<Translate value="corgi.search.explore.trending" />
							</Text>
						}
						onClick={filterByTrending}
						disabled={disabled}
						data-qa="explore-trending-button"
					/>
					<ExploreButton
						icon={<ChangeFlagIcon size="x-large" gradient />}
						text={
							<Text variant="body" as="span" size="large" className="text-center font-semibold">
								<Translate value="corgi.search.explore.victories" />
							</Text>
						}
						onClick={filterByVictories}
						disabled={disabled}
						data-qa="explore-victories-button"
					/>
				</div>
			</div>
		</div>
	);
};

const ExploreButton: React.FC<{
	icon: React.ReactNode;
	text: React.ReactNode;
	onClick?: () => void;
	disabled?: boolean;
}> = ({ icon, text, onClick, disabled }) => {
	return (
		<Button
			variant="secondary"
			className="elevation-elevated active:elevation-elevated-selected hover:elevation-elevated-selected focus-visible:elevation-elevated-selected h-auto rounded-lg px-0 py-6 before:border-none"
			onClick={onClick}
			disabled={disabled}
		>
			<div className="flex flex-col items-center justify-center gap-2">
				{icon}
				{text}
			</div>
		</Button>
	);
};
